import React, { useState, useContext, useEffect } from 'react';
import TopNavBar from './TopNavBar';

// Import configs
import { AuthContext } from '../AuthContext';
import { auth } from '../config/firebaseConfig'; 

// Import functions
import {  
  fetchClientProgramsFromFirestore, 
  fetchClientOrganizationsFromFirestore, 
  fetchMembershipDetailsFromFirestoreForOrganization,
} from '../utilityFunctions/firestoreUtilities';

// Import components
import UserSimulator from './HelperComponents/UserSimulator';
import LoadingModal from './HelperComponents/LoadingModal';
import PaywallComponent from './HelperComponents/PaywallComponent';
import CreateProgramModal from './ProgramComponents/CreateProgramModal';
import ManageProgramModal from './ProgramComponents/ManageProgramModal';
import LoginModal from './AuthComponents/LoginModal';
import SignupModal from './AuthComponents/SignupModal'; 
import ChooseProgramModal from './ProgramComponents/ChooseProgramModal';
import MagicFirstDraftModal from './WritingComponents/MagicFirstDraftModal';

import AwardsSearch from './AwardsSearch/AwardsSearch';
import OrganizationProfilePage from './OrgProfileComponents/OrganizationProfilePage';

function Home({
}) {

  const [referrerOrganizationEin, setReferrerOrganizationEin] = useState(null);

  const demoPrograms = [
    {
        programName: 'World Central Kitchen (FL)',
        primaryState: 'FL',
        nteeFull: 'Q33',
    },
    {
        programName: 'Meals on Wheels (AK)',
        primaryState: 'AK',
        nteeFull: 'K36',
    },
]   

  const { authUser } = useContext(AuthContext);  

  // Top level state variables
  const [currentView, setCurrentView] = useState('AwardsSearch'); 
  const [membershipDetails, setMembershipDetails] = useState(null);
  const [clientOrganizationsBelongedTo, setClientOrganizationsBelongedTo] = useState([]);
  const [currentClientOrganization, setCurrentClientOrganization] = useState(null);
  const [organizationPrograms, setOrganizationPrograms] = useState([]);  

  const [currentProgram, setCurrentProgram] = useState(null)
  // const [programToModify, setProgramToModify] = useState(null)

  const [showLoadingModal, setShowLoadingModal] = useState(false)
  const [showPaywallModal, setShowPaywallModal] = useState(false)
  const [showCreateProgramModal, setShowCreateProgramModal] = useState(false)
  const [showManageProgramModal, setShowManageProgramModal] = useState(false);
  const [showChooseProgramModal, setShowChooseProgramModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMagicFirstDraftModal, setShowMagicFirstDraftModal] = useState(false);

  const [funderNameForMagicDraft, setFunderNameForMagicDraft] = useState('');
  const [funderDescriptionForMagicDraft, setFunderDescriptionForMagicDraft] = useState('');

  // When authUser changes, get client organizations
  useEffect(() => {        
    if (authUser) { 
      if (authUser.uid) { 

        fetchClientOrganizationsBelongedTo(authUser.uid);

        window.posthog.identify(authUser.uid);
      } 
    } else {
      console.log("No authUser found, setting current program to first demo program");
      setCurrentProgram(demoPrograms[0]);
    }
  }, [authUser]); 

  // Define current client organization
  useEffect(() => {
    if (clientOrganizationsBelongedTo.length > 0) {
      setCurrentClientOrganization(clientOrganizationsBelongedTo[0])  // TODO: Provide a way to select a client organization
    } 
  }, [clientOrganizationsBelongedTo]);

  // Once current client organization set, fetch programs, membership details, and refresh grant proposals
  useEffect(() => {
    if (currentClientOrganization)  {
      fetchProgramsForOrganization(currentClientOrganization.id);
      fetchMembershipDetails()   
    }
  }, [currentClientOrganization]);

  // Check URL for organization EIN and set view accordingly
  useEffect(() => {
    const checkUrlForOrganization = () => {
      const pathname = window.location.pathname;
      console.log("Checking URL pathname:", pathname);
      const organizationMatch = pathname.match(/\/organizations\/(\d+)/);
      
      if (organizationMatch) {
        const ein = organizationMatch[1];
        console.log("Organization EIN found in URL:", ein);
        setCurrentView("organizationProfilePage");
        setReferrerOrganizationEin(ein);
      } else {
        console.log("No organization EIN found in URL, staying on current view:", currentView);
      }
    };

    // Check URL on initial load
    checkUrlForOrganization();

    // Listen for browser back/forward navigation
    const handlePopState = () => {
      console.log("Popstate event detected, checking URL");
      checkUrlForOrganization();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentView]);

  const fetchProgramsForOrganization = async (organizationId) => {
    console.log("Fetching client programs for organization ID:", organizationId);
    const clientPrograms = await fetchClientProgramsFromFirestore(organizationId);
    console.log("Fetched client programs:", clientPrograms);
    setOrganizationPrograms(clientPrograms);
    setCurrentProgram(clientPrograms[0]);

    if (!clientPrograms || clientPrograms.length === 0) {
      console.log("Current client organization found, but no programs found, showing create program modal");
      setShowCreateProgramModal(true);
    }
    return clientPrograms;
  };

  // Main function to fetch clientOrganizations which user belongs to
  const fetchClientOrganizationsBelongedTo = async (uid) => {
    try {
        const clientOrganizations = await fetchClientOrganizationsFromFirestore(uid);
        setClientOrganizationsBelongedTo(clientOrganizations);
        return clientOrganizations;
    } catch (error) {
        console.error("Error fetching client organizations: ", error);
        throw error;
    }
  };

  // Main function to fetch plan details
  const fetchMembershipDetails = async () => {
    if (!auth.currentUser || !currentClientOrganization) {
        console.error("No current user / current clientOrg found.");
        return;
    }
    try {
        const membershipDetails = await fetchMembershipDetailsFromFirestoreForOrganization(currentClientOrganization.id);
        console.log("membershipDetails: ", membershipDetails);
        setMembershipDetails(membershipDetails)
    } catch (error) {
        console.error("Error fetching membership details: ", error);
    }
  };

  // useEffect(() => {
  //   if (programToModify) {
  //     setCurrentProgram(programToModify);
  //     setShowManageProgramModal(true);
  //   }
  // }, [programToModify]);


  const renderView = () => {
    switch (currentView) {

      case 'AwardsSearch':
        return <AwardsSearch 
          currentProgram={currentProgram}
          setShowManageProgramModal={setShowManageProgramModal}
          setShowLoadingModal={setShowLoadingModal}          
          setShowLoginModal={setShowLoginModal}    
          setShowChooseProgramModal={setShowChooseProgramModal}
          membershipDetails={membershipDetails}
          setShowPaywallModal={setShowPaywallModal}
        />;
      
      case 'organizationProfilePage':
        return <OrganizationProfilePage 
          currentProgram={currentProgram}
          referrerOrganizationEin={referrerOrganizationEin}
          setShowMagicFirstDraftModal={setShowMagicFirstDraftModal}
          setShowLoadingModal={setShowLoadingModal}
          membershipDetails={membershipDetails}
          setShowPaywallModal={setShowPaywallModal}          
          setFunderNameForMagicDraft={setFunderNameForMagicDraft}
          setFunderDescriptionForMagicDraft={setFunderDescriptionForMagicDraft}
        />;

    }
  };

  return (
  <div className="">
    
    {/* Modals */}
    <div>          
      {/* {true && (
        <UserSimulator 
          fetchClientOrganizationsBelongedTo={fetchClientOrganizationsBelongedTo}   
          setCurrentProgram={setCurrentProgram}
          fetchProgramsForOrganization={fetchProgramsForOrganization}           
          refreshOpportunitiesForCurrentProgram={refreshOpportunitiesForCurrentProgram}
          organizationPrograms={organizationPrograms}
        />
      )} */}

      {showLoadingModal && (
        <LoadingModal              
        />
      )}

      {showPaywallModal && (
        <PaywallComponent
          onClose={() => setShowPaywallModal(false)}           
        />
      )}

      {showLoginModal && (
        <LoginModal
          setShowLoginModal={setShowLoginModal} 
          setShowSignupModal={setShowSignupModal}
        />
      )}

      {showSignupModal && (
        <SignupModal
          setShowSignupModal={setShowSignupModal}
          setShowLoginModal={setShowLoginModal}
          setShowLoadingModal={setShowLoadingModal}
        />
      )}

      {showMagicFirstDraftModal && (
        <MagicFirstDraftModal
          setShowMagicFirstDraftModal={setShowMagicFirstDraftModal}
          currentProgram={currentProgram}
          setShowLoadingModal={setShowLoadingModal}
          currentClientOrganization={currentClientOrganization}
          prepopulatedFunderName=""
          prepopulatedFunderDescription=""
          organizationPrograms={organizationPrograms}
          funderNameForMagicDraft={funderNameForMagicDraft}
          funderDescriptionForMagicDraft={funderDescriptionForMagicDraft}
        />
      )}

      {showManageProgramModal && (
        <ManageProgramModal
          currentProgram={currentProgram}
          currentClientOrganization={currentClientOrganization}
          setShowManageProgramModal={setShowManageProgramModal}
          fetchProgramsForOrganization={fetchProgramsForOrganization}
          setCurrentProgram={setCurrentProgram}
          setShowCreateProgramModal={setShowCreateProgramModal}
        />
      )}
      
      {showCreateProgramModal && (
        <CreateProgramModal         
          currentClientOrganization={currentClientOrganization} 
          organizationPrograms={organizationPrograms}
          // existingProgram={programToModify}
          fetchProgramsForOrganization={fetchProgramsForOrganization}
          setCurrentProgram={setCurrentProgram}
          setShowCreateProgramModal={setShowCreateProgramModal}
        />
      )}
      
      {showChooseProgramModal && (
          <ChooseProgramModal 
              demoPrograms={demoPrograms}
              organizationPrograms={organizationPrograms}
              setCurrentProgram={setCurrentProgram}
              setShowChooseProgramModal={setShowChooseProgramModal}
              setShowCreateProgramModal={setShowCreateProgramModal}
              setShowLoginModal={setShowLoginModal}
              setShowSignupModal={setShowSignupModal}
              // setProgramToModify={setProgramToModify}
              setShowManageProgramModal={setShowManageProgramModal}
              membershipDetails={membershipDetails}
              currentProgram={currentProgram}
              setShowPaywallModal={setShowPaywallModal}              
          />
      )}

    </div>           
    {/* Top Nav Bar */}
    <TopNavBar 
      setShowLoginModal={setShowLoginModal}
      setShowSignupModal={setShowSignupModal}    
    />

    <div className='pt-16'>
      {renderView()}
    </div>
  </div>
  );
}

export default Home;